import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Table, Tag, Button, Input, Select, DatePicker, Space, Popconfirm,
  Tooltip, Modal, Typography, Badge, Card, Row, Col, Statistic, Radio,
} from "antd";
import {
  SearchOutlined, DeleteOutlined, ReloadOutlined, ClearOutlined, EyeOutlined,
  ApiOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import ReactApexChart from "react-apexcharts";
import {
  fetchApiLogs, fetchApiLogStats, fetchApiLogTimeSeries,
  fetchDistinctRoutes, deleteApiLog, deleteSelectedApiLogs, clearAllApiLogs,
} from "../../../core/data/redux/slices/ApiLogsSlice";

const { RangePicker } = DatePicker;
const { Text, Paragraph } = Typography;

const METHOD_COLORS = { GET: "blue", POST: "green", PUT: "orange", PATCH: "gold", DELETE: "red" };
const STATUS_COLOR  = (code) => {
  if (!code) return "default";
  if (code < 300) return "success";
  if (code < 400) return "processing";
  if (code < 500) return "warning";
  return "error";
};

// ── Charts (all driven by server-side aggregation, not paginated logs) ────────
const METHOD_COLOR_MAP = { GET: "#3b82f6", POST: "#22c55e", PUT: "#f97316", PATCH: "#eab308", DELETE: "#ef4444" };
const STATUS_BUCKET_COLOR = { 2: "#22c55e", 3: "#3b82f6", 4: "#f59e0b", 5: "#ef4444" };

function MethodDonut({ data }) {
  if (!data?.length) return null;
  return (
    <ReactApexChart
      type="donut"
      height={220}
      options={{
        labels: data.map((d) => d._id),
        colors: data.map((d) => METHOD_COLOR_MAP[d._id] || "#6366f1"),
        legend: { position: "bottom", fontSize: "12px" },
        dataLabels: { enabled: true, formatter: (v) => `${v.toFixed(0)}%` },
        plotOptions: { pie: { donut: { size: "60%" } } },
        tooltip: { y: { formatter: (v) => `${v} requests` } },
      }}
      series={data.map((d) => d.count)}
    />
  );
}

function StatusBar({ data }) {
  if (!data?.length) return null;
  const labels = data.map((d) => `${d._id}xx`);
  const colors = data.map((d) => STATUS_BUCKET_COLOR[d._id] || "#9ca3af");
  return (
    <ReactApexChart
      type="bar"
      height={220}
      options={{
        chart: { toolbar: { show: false } },
        xaxis: { categories: labels },
        colors,
        plotOptions: { bar: { borderRadius: 4, distributed: true, columnWidth: "40%" } },
        legend: { show: false },
        dataLabels: { enabled: false },
        yaxis: { labels: { formatter: (v) => Math.round(v) } },
        tooltip: { y: { formatter: (v) => `${v} requests` } },
      }}
      series={[{ name: "Requests", data: data.map((d) => d.count) }]}
    />
  );
}

function fillGaps(data, groupBy) {
  if (!data?.length) return [];
  const countMap = {};
  data.forEach((d) => { countMap[d._id] = d.count; });

  const first = data[0]._id;
  const last  = data[data.length - 1]._id;
  const filled = [];

  if (groupBy === "hour") {
    let cur = dayjs(first, "YYYY-MM-DD HH:00");
    const end = dayjs(last, "YYYY-MM-DD HH:00");
    while (!cur.isAfter(end)) {
      const key = cur.format("YYYY-MM-DD HH:00");
      filled.push({ _id: key, count: countMap[key] || 0 });
      cur = cur.add(1, "hour");
    }
  } else if (groupBy === "day") {
    let cur = dayjs(first, "YYYY-MM-DD");
    const end = dayjs(last, "YYYY-MM-DD");
    while (!cur.isAfter(end)) {
      const key = cur.format("YYYY-MM-DD");
      filled.push({ _id: key, count: countMap[key] || 0 });
      cur = cur.add(1, "day");
    }
  } else if (groupBy === "month") {
    let cur = dayjs(first + "-01");
    const end = dayjs(last + "-01");
    while (!cur.isAfter(end)) {
      const key = cur.format("YYYY-MM");
      filled.push({ _id: key, count: countMap[key] || 0 });
      cur = cur.add(1, "month");
    }
  } else {
    // week: no simple iteration, return as-is
    return data;
  }
  return filled.length ? filled : data;
}

function TimeSeries({ data, groupBy }) {
  if (!data?.length) return (
    <div style={{ height: 260, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af" }}>
      No data for selected filters
    </div>
  );

  const filled = fillGaps(data, groupBy);
  const labels = filled.map((d) => d._id);
  const counts = filled.map((d) => d.count);
  const tooltipLabel = { hour: "hour", day: "day", week: "week", month: "month" }[groupBy] || "period";
  const rotate = labels.length > 30 ? -60 : labels.length > 14 ? -45 : -30;
  const fontSize = labels.length > 30 ? "9px" : "10px";

  return (
    <ReactApexChart
      key={`${groupBy}-${labels.length}`}
      type="area"
      height={260}
      options={{
        chart: {
          id: "requests-over-time",
          toolbar: { show: true, tools: { download: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true } },
          zoom: { enabled: true },
          animations: { enabled: false },
        },
        xaxis: {
          categories: labels,
          labels: { rotate, style: { fontSize }, hideOverlappingLabels: true },
          tickAmount: Math.min(labels.length, 20),
        },
        yaxis: { labels: { formatter: (v) => Math.round(v) } },
        colors: ["#6366f1"],
        fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.03 } },
        stroke: { curve: "smooth", width: 2 },
        dataLabels: { enabled: false },
        markers: { size: labels.length <= 20 ? 4 : 0, colors: ["#6366f1"], strokeWidth: 0 },
        tooltip: {
          x: { show: true },
          y: { formatter: (v) => `${v} requests` },
        },
        grid: { borderColor: "#f1f5f9", strokeDashArray: 4 },
      }}
      series={[{ name: `Requests per ${tooltipLabel}`, data: counts }]}
    />
  );
}

function TopRoutesBar({ data }) {
  if (!data?.length) return null;
  return (
    <ReactApexChart
      type="bar"
      height={220}
      options={{
        chart: { toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 4, horizontal: true } },
        colors: ["#6366f1"],
        xaxis: { categories: data.map((d) => d._id || "(none)") },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (v) => `${v} hits` } },
        grid: { borderColor: "#f1f5f9" },
      }}
      series={[{ name: "Hits", data: data.map((d) => d.count) }]}
    />
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function AdminApiLogs() {
  const dispatch = useDispatch();
  const { logs, total, loading, distinctRoutes, stats, statsLoading, timeSeries, timeSeriesLoading } = useSelector((s) => s.apiLogs);

  const [filters, setFilters] = useState({
    page: 1, limit: 50,
    method: undefined, statusCode: undefined,
    baseRoute: undefined, search: "", startDate: undefined, endDate: undefined,
  });
  const [detailLog, setDetailLog] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [groupBy, setGroupBy] = useState("hour");

  // Strip page/limit before sending to stats endpoint; inject current groupBy
  const buildStatsParams = (state, gb = groupBy) => {
    const { page, limit, ...rest } = state;
    const p = { ...rest, groupBy: gb };
    Object.keys(p).forEach((k) => { if (p[k] === undefined || p[k] === "") delete p[k]; });
    return p;
  };

  const buildParams = (state) => {
    const p = { ...state };
    Object.keys(p).forEach((k) => { if (p[k] === undefined || p[k] === "") delete p[k]; });
    return p;
  };

  const applyFilters = useCallback((overrides = {}) => {
    const merged = { ...filters, ...overrides };
    dispatch(fetchApiLogs(buildParams(merged)));
  }, [dispatch, filters]);

  useEffect(() => {
    dispatch(fetchDistinctRoutes());
    dispatch(fetchApiLogStats({ groupBy }));
    dispatch(fetchApiLogTimeSeries({ groupBy }));
    applyFilters();
  }, []);

  const handleGroupByChange = (gb) => {
    setGroupBy(gb);
    // Only fetch time series — leaves all other charts/KPIs untouched
    const p = buildStatsParams(filters, gb);
    dispatch(fetchApiLogTimeSeries(p));
  };

  // Filter changes — refresh table, charts, and time series
  const updateFilter = (key, value) => {
    setSelectedRowKeys([]);
    const updated = { ...filters, [key]: value, page: 1 };
    setFilters(updated);
    dispatch(fetchApiLogs(buildParams(updated)));
    dispatch(fetchApiLogStats(buildStatsParams(updated)));
    dispatch(fetchApiLogTimeSeries({ ...buildStatsParams(updated), groupBy }));
  };

  const handleDateRange = (dates) => {
    const updated = { ...filters, startDate: dates?.[0]?.toISOString(), endDate: dates?.[1]?.toISOString(), page: 1 };
    setFilters(updated);
    dispatch(fetchApiLogs(buildParams(updated)));
    dispatch(fetchApiLogStats(buildStatsParams(updated)));
    dispatch(fetchApiLogTimeSeries({ ...buildStatsParams(updated), groupBy }));
  };

  // Pagination only — table changes, charts stay
  const handlePageChange = (page, pageSize) => {
    setSelectedRowKeys([]);
    const updated = { ...filters, page, limit: pageSize };
    setFilters(updated);
    dispatch(fetchApiLogs(buildParams(updated)));
  };

  const columns = [
    {
      title: "Time",
      dataIndex: "createdAt",
      width: 155,
      render: (v) => <span style={{ fontSize: 12, color: "#6b7280" }}>{dayjs(v).format("DD MMM YY HH:mm:ss")}</span>,
    },
    {
      title: "Method",
      dataIndex: "method",
      width: 85,
      render: (m) => <Tag color={METHOD_COLORS[m] || "default"}>{m}</Tag>,
    },
    {
      title: "URL",
      dataIndex: "url",
      ellipsis: true,
      render: (url) => (
        <Tooltip title={url}>
          <Text style={{ fontSize: 12, fontFamily: "monospace" }}>{url}</Text>
        </Tooltip>
      ),
    },
    {
      title: "Status",
      dataIndex: "statusCode",
      width: 80,
      render: (code) => <Badge status={STATUS_COLOR(code)} text={code || "—"} />,
    },
    {
      title: "Time (ms)",
      dataIndex: "responseTime",
      width: 95,
      render: (ms) => {
        if (ms == null) return "—";
        const color = ms > 2000 ? "#ef4444" : ms > 500 ? "#f59e0b" : "#10b981";
        return <span style={{ color, fontWeight: 600, fontSize: 12 }}>{ms}ms</span>;
      },
    },
    {
      title: "User",
      dataIndex: "userName",
      width: 150,
      ellipsis: true,
      render: (name, rec) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12 }}>{name || "—"}</Text>
          {rec.userEmail && <Text style={{ fontSize: 11, color: "#9ca3af" }}>{rec.userEmail}</Text>}
        </Space>
      ),
    },
    {
      title: "Role",
      dataIndex: "userRole",
      width: 110,
      render: (role) =>
        role ? (
          <Tag color={role === "superadmin" ? "purple" : role === "companyAdmin" ? "cyan" : "default"} style={{ fontSize: 11 }}>
            {role}
          </Tag>
        ) : "—",
    },
    {
      title: "IP",
      dataIndex: "ip",
      width: 115,
      render: (ip) => <Text style={{ fontSize: 12, color: "#6b7280" }}>{ip || "—"}</Text>,
    },
    {
      title: "",
      width: 70,
      render: (_, record) => (
        <Space>
          <Tooltip title="View details">
            <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => setDetailLog(record)} />
          </Tooltip>
          <Popconfirm title="Delete this log?" onConfirm={() => dispatch(deleteApiLog(record._id))} okButtonProps={{ danger: true }} okText="Delete">
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <h3 style={{ margin: 0, fontWeight: 700 }}>API Logs</h3>
            <span style={{ color: "#6b7280" }}>All incoming API requests — auto-cleared after 30 days</span>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => { applyFilters(); dispatch(fetchApiLogStats(buildStatsParams(filters))); dispatch(fetchApiLogTimeSeries({ ...buildStatsParams(filters), groupBy })); }}>Refresh</Button>
            {selectedRowKeys.length > 0 && (
              <Popconfirm
                title={`Delete ${selectedRowKeys.length} selected log${selectedRowKeys.length > 1 ? "s" : ""}?`}
                onConfirm={() => dispatch(deleteSelectedApiLogs(selectedRowKeys)).then(() => setSelectedRowKeys([]))}
                okButtonProps={{ danger: true }}
                okText="Delete"
              >
                <Button danger icon={<DeleteOutlined />}>Delete Selected ({selectedRowKeys.length})</Button>
              </Popconfirm>
            )}
            <Popconfirm title="Clear ALL logs? This cannot be undone." onConfirm={() => dispatch(clearAllApiLogs())} okButtonProps={{ danger: true }} okText="Clear All">
              <Button danger icon={<ClearOutlined />}>Clear All</Button>
            </Popconfirm>
          </Space>
        </div>

        {/* KPI cards */}
        <Row gutter={16} style={{ marginBottom: 20 }}>
          {[
            { title: "Total Requests", value: stats?.summary?.total ?? total, icon: <ApiOutlined />, color: "#6366f1" },
            {
              title: "Success Rate",
              value: stats?.summary?.total
                ? `${Math.round(((stats.summary.success) / stats.summary.total) * 100)}%`
                : "—",
              icon: <CheckCircleOutlined />, color: "#22c55e",
            },
            { title: "Errors (5xx)", value: stats?.summary?.errors5xx ?? "—", icon: <CloseCircleOutlined />, color: "#ef4444" },
            {
              title: "Avg Response",
              value: stats?.summary?.avgResponseTime != null
                ? `${Math.round(stats.summary.avgResponseTime)}ms`
                : "—",
              icon: <ClockCircleOutlined />, color: "#f59e0b",
            },
          ].map(({ title, value, icon, color }) => (
            <Col span={6} key={title}>
              <Card size="small" style={{ borderRadius: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }} loading={statsLoading}>
                <Statistic
                  title={<span style={{ fontSize: 13, color: "#6b7280" }}>{title}</span>}
                  value={value}
                  prefix={React.cloneElement(icon, { style: { color } })}
                  valueStyle={{ fontSize: 22, fontWeight: 700 }}
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Charts */}
        <Row gutter={16} style={{ marginBottom: 20 }}>
          <Col span={6}>
            <Card size="small" title="Requests by Method" style={{ borderRadius: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }} loading={statsLoading}>
              <MethodDonut data={stats?.methodBreakdown} />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" title="Status Codes" style={{ borderRadius: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }} loading={statsLoading}>
              <StatusBar data={stats?.statusBreakdown} />
            </Card>
          </Col>
          <Col span={12}>
            <Card
              size="small"
              title="Requests Over Time"
              style={{ borderRadius: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}
              loading={timeSeriesLoading}
            >
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                <Radio.Group
                  size="small"
                  optionType="button"
                  buttonStyle="solid"
                  value={groupBy}
                  onChange={(e) => handleGroupByChange(e.target.value)}
                  options={[
                    { label: "Hourly", value: "hour" },
                    { label: "Daily", value: "day" },
                    { label: "Weekly", value: "week" },
                    { label: "Monthly", value: "month" },
                  ]}
                />
              </div>
              <TimeSeries data={timeSeries} groupBy={groupBy} />
            </Card>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 20 }}>
          <Col span={24}>
            <Card size="small" title="Top Routes" style={{ borderRadius: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }} loading={statsLoading}>
              <TopRoutesBar data={stats?.topRoutes} />
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <div style={{
          background: "#fff", borderRadius: 10, padding: "14px 18px",
          boxShadow: "0 1px 6px rgba(0,0,0,0.06)", marginBottom: 16,
          display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center",
        }}>
          <Input
            placeholder="Search URL..."
            prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
            style={{ width: 210 }}
            value={filters.search}
            onChange={(e) => updateFilter("search", e.target.value)}
            allowClear
          />
          <Select
            placeholder="Method"
            style={{ width: 110 }}
            allowClear
            value={filters.method}
            onChange={(v) => updateFilter("method", v)}
            options={["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => ({
              label: <Tag color={METHOD_COLORS[m]}>{m}</Tag>, value: m,
            }))}
          />
          <Select
            placeholder="Status"
            style={{ width: 130 }}
            allowClear
            value={filters.statusCode}
            onChange={(v) => updateFilter("statusCode", v)}
            options={[
              { label: "200 OK", value: "200" },
              { label: "201 Created", value: "201" },
              { label: "204 No Content", value: "204" },
              { label: "400 Bad Request", value: "400" },
              { label: "401 Unauthorized", value: "401" },
              { label: "403 Forbidden", value: "403" },
              { label: "404 Not Found", value: "404" },
              { label: "500 Server Error", value: "500" },
            ]}
          />
          <Select
            placeholder="Route"
            style={{ width: 170 }}
            allowClear
            showSearch
            value={filters.baseRoute}
            onChange={(v) => updateFilter("baseRoute", v)}
            options={distinctRoutes.map((r) => ({ label: r, value: r }))}
          />
          <RangePicker style={{ width: 240 }} onChange={handleDateRange} disabledDate={(d) => d && d > dayjs().endOf("day")} />
          <Button
            onClick={() => {
              setSelectedRowKeys([]);
              const reset = { page: 1, limit: 50, method: undefined, statusCode: undefined, baseRoute: undefined, search: "", startDate: undefined, endDate: undefined };
              setFilters(reset);
              dispatch(fetchApiLogs({ page: 1, limit: 50 }));
              dispatch(fetchApiLogStats({ groupBy }));
              dispatch(fetchApiLogTimeSeries({ groupBy }));
            }}
          >
            Reset
          </Button>
          <span style={{ marginLeft: "auto", color: "#6b7280", fontSize: 13 }}>{total.toLocaleString()} total</span>
        </div>

        {/* Table */}
        <div style={{ background: "#fff", borderRadius: 10, padding: "0 0 8px", boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}>
          <Table
            columns={columns}
            dataSource={logs}
            rowKey="_id"
            loading={loading}
            size="small"
            scroll={{ x: 1000 }}
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
              preserveSelectedRowKeys: false,
            }}
            pagination={{
              current: filters.page,
              pageSize: filters.limit,
              total,
              showSizeChanger: true,
              pageSizeOptions: ["25", "50", "100"],
              showTotal: (t, [from, to]) => `${from}–${to} of ${t}`,
              onChange: handlePageChange,
            }}
          />
        </div>

        {/* Detail Modal */}
        <Modal
          title={
            <Space>
              <Tag color={METHOD_COLORS[detailLog?.method]}>{detailLog?.method}</Tag>
              <Text style={{ fontFamily: "monospace", fontSize: 13 }}>{detailLog?.url}</Text>
            </Space>
          }
          open={!!detailLog}
          onCancel={() => setDetailLog(null)}
          footer={null}
          width={680}
        >
          {detailLog && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div><Text type="secondary">Status</Text><br /><Badge status={STATUS_COLOR(detailLog.statusCode)} text={detailLog.statusCode || "—"} /></div>
              <div><Text type="secondary">Response Time</Text><br /><Text strong>{detailLog.responseTime != null ? `${detailLog.responseTime}ms` : "—"}</Text></div>
              <div><Text type="secondary">User</Text><br /><Text>{detailLog.userName || "—"}</Text><br /><Text style={{ fontSize: 12, color: "#9ca3af" }}>{detailLog.userEmail || ""}</Text></div>
              <div><Text type="secondary">Role</Text><br /><Text>{detailLog.userRole || "—"}</Text></div>
              <div><Text type="secondary">IP</Text><br /><Text>{detailLog.ip || "—"}</Text></div>
              <div><Text type="secondary">Time</Text><br /><Text>{dayjs(detailLog.createdAt).format("DD MMM YYYY, HH:mm:ss")}</Text></div>
              <div style={{ gridColumn: "1 / -1" }}>
                <Text type="secondary">Query Params</Text>
                <pre style={{ background: "#f9fafb", borderRadius: 6, padding: "10px 14px", fontSize: 12, margin: "4px 0 0", overflow: "auto" }}>
                  {Object.keys(detailLog.query || {}).length
                    ? JSON.stringify(detailLog.query, null, 2)
                    : "(none)"}
                </pre>
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <Text type="secondary">Request Body</Text>
                <pre style={{ background: "#f9fafb", borderRadius: 6, padding: "10px 14px", fontSize: 12, margin: "4px 0 0", overflow: "auto", maxHeight: 300 }}>
                  {Object.keys(detailLog.body || {}).length
                    ? JSON.stringify(detailLog.body, null, 2)
                    : "(none)"}
                </pre>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
}
